/**
 * KEJJA — Routines · Today (v2)
 * Vertical tab rail (Family / Me / per-kid)
 * Per-person streaks · Big tappable cards · Confetti + on-time scoring
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Home, User, Check, Flame, Plus, Gift, Trophy, ChevronRight,
  Users, Baby, Loader2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { confettiBurst } from '@/lib/confetti';
import { toast } from 'sonner';
import { useMyHomes } from '@/hooks/useMe';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Routine {
  id: string;
  homeId: string;
  userId: string | null;
  kidProfileId: string | null;
  scope: 'family' | 'personal' | 'kid';
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  cadence: string;
  timeOfDay: string | null;
  isActive: boolean;
  completed?: boolean;
  completedToday?: boolean;
  isDueToday?: boolean;
  streak?: number;
  minutesLate?: number | null;
  pointsAwarded?: number | null;
}

interface KidProfile {
  id: string; homeId: string; name: string;
  avatarEmoji: string; color: string;
  currentStreakDays: number; totalCheckins: number; routineCount: number;
  isActive: boolean;
}

interface Reward {
  id: string; kidProfileId: string | null; userId: string | null;
  streakTarget: number; rewardLabel: string; rewardValueMinor: number | null;
  earnedAt: string | null; givenAt: string | null;
  kidName: string | null; userName: string | null; routineName: string | null;
}

type Tab = { id: string; label: string; icon: React.ElementType; kind: 'family' | 'me' | 'kid'; kidId?: string };

export default function TodayPageV2() {
  const { data: homesData } = useMyHomes();
  const { user } = useAuth();
  const home = homesData?.homes?.[0]?.home;
  const homeId = home?.id;
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>('family');

  const { data: routinesData, isLoading: loadingR } = useQuery({
    queryKey: ['routines', homeId, 'today-v2'],
    queryFn: () => api.get<{ routines: Routine[] }>(`/homes/${homeId}/routines/today`),
    enabled: !!homeId,
  });
  const { data: kidsData } = useQuery({
    queryKey: ['kids', homeId],
    queryFn: () => api.get<{ kids: KidProfile[] }>(`/homes/${homeId}/kids`),
    enabled: !!homeId,
  });
  const { data: rewardsData } = useQuery({
    queryKey: ['rewards', homeId, 'unclaimed'],
    queryFn: () => api.get<{ rewards: Reward[] }>(`/homes/${homeId}/kids/rewards/list?unclaimed=1`),
    enabled: !!homeId,
  });

  const routines = routinesData?.routines ?? [];
  const kids = (kidsData?.kids ?? []).filter(k => k.isActive);
  const rewards = rewardsData?.rewards ?? [];

  const tabs: Tab[] = useMemo(() => [
    { id: 'family', label: 'Family',  icon: Home, kind: 'family' },
    { id: 'me',     label: 'Me',      icon: User, kind: 'me' },
    ...kids.map(k => ({ id: `kid:${k.id}`, label: k.name, icon: Baby, kind: 'kid' as const, kidId: k.id })),
  ], [kids]);

  const filtered = useMemo(() => {
    const t = tabs.find(x => x.id === activeTab);
    if (!t) return [];
    if (t.kind === 'family') return routines.filter(r => r.scope === 'family' || (!r.userId && !r.kidProfileId));
    if (t.kind === 'me') return routines.filter(r => r.userId === user?.id);
    if (t.kind === 'kid') return routines.filter(r => r.kidProfileId === t.kidId);
    return [];
  }, [tabs, activeTab, routines, user]);

  const checkMut = useMutation({
    mutationFn: async (p: { routineId: string; kidProfileId?: string | null; clickXY?: { x: number; y: number } }) => {
      const res = await api.post<{ checkin: { scoring?: { points: number; minutesLate: number; bucket: string; message: string } } }>(
        `/homes/${homeId}/routines/${p.routineId}/checkoff`,
        { kidProfileId: p.kidProfileId ?? null }
      );
      return { ...res, _click: p.clickXY };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['routines', homeId] });
      qc.invalidateQueries({ queryKey: ['rewards', homeId] });
      const sc = data?.checkin?.scoring;
      const click = (data as any)._click ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      if (sc) {
        if (sc.bucket === 'on_time') {
          confettiBurst(click, '🎉', 18);
          toast.success(`+${sc.points} · ${sc.message}`);
        } else if (sc.bucket === 'within_hour') {
          confettiBurst(click, '⭐', 8);
          toast.success(`+${sc.points} · ${sc.message}`);
        } else if (sc.bucket === 'within_3h') {
          toast(`+${sc.points} · ${sc.message}`, { icon: '⏰' });
        } else {
          toast.error(sc.message, { icon: '😬' });
        }
      } else {
        confettiBurst(click, '✨', 10);
        toast.success('Done ✓');
      }
    },
  });

  const uncheckMut = useMutation({
    mutationFn: (routineId: string) => api.delete(`/homes/${homeId}/routines/${routineId}/checkoff`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routines', homeId] }),
  });

  if (!homeId) return null;
  const activeTabObj = tabs.find(t => t.id === activeTab);
  const activeKid = kids.find(k => k.id === activeTabObj?.kidId);
  const myRewards = rewards.filter(r => activeTabObj?.kind === 'kid'
    ? r.kidProfileId === activeTabObj.kidId
    : activeTabObj?.kind === 'me' ? r.userId === user?.id : false);

  const completedCount = filtered.filter(r => r.completedToday).length;

  return (
    <div className="grid gap-5 pb-20" style={{ gridTemplateColumns: "minmax(180px, 220px) 1fr" }}>
      {/* ============== VERTICAL TAB RAIL ============== */}
      <aside className="space-y-1 sticky top-4 self-start">
        <div className="px-2 pb-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Routines</div>
        {tabs.map(t => {
          const Icon = t.icon;
          const isKid = t.kind === 'kid';
          const kid = isKid ? kids.find(k => k.id === t.kidId) : null;
          const isActive = activeTab === t.id;
          // Count routines + done for this tab
          let count = 0, done = 0;
          if (t.kind === 'family') {
            count = routines.filter(r => r.scope === 'family' || (!r.userId && !r.kidProfileId)).length;
            done  = routines.filter(r => (r.scope === 'family' || (!r.userId && !r.kidProfileId)) && r.completedToday).length;
          } else if (t.kind === 'me') {
            count = routines.filter(r => r.userId === user?.id).length;
            done  = routines.filter(r => r.userId === user?.id && r.completedToday).length;
          } else if (t.kind === 'kid') {
            count = routines.filter(r => r.kidProfileId === t.kidId).length;
            done  = routines.filter(r => r.kidProfileId === t.kidId && r.completedToday).length;
          }
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition border-l-4',
                isActive
                  ? 'bg-sky-500/10 border-sky-500 text-sky-700 dark:text-sky-300'
                  : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {kid ? (
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                      style={{ background: kid.color + '25' }}>{kid.avatarEmoji}</span>
              ) : (
                <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4" />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{t.label}</div>
                <div className="text-[10px] flex items-center gap-2">
                  <span className="text-muted-foreground">{done}/{count}</span>
                  {kid && kid.currentStreakDays > 0 && (
                    <span className="text-orange-600 dark:text-orange-400 flex items-center gap-0.5">
                      <Flame className="w-2.5 h-2.5" />{kid.currentStreakDays}d
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </aside>

      {/* ============== CONTENT COLUMN ============== */}
      <div className="space-y-5 min-w-0">
        {activeTabObj?.kind === 'kid' && activeKid && (
          <Card style={{ borderColor: activeKid.color + '40' }} className="overflow-hidden">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-3xl flex-shrink-0"
                   style={{ background: activeKid.color + '20' }}>
                {activeKid.avatarEmoji}
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold">{activeKid.name}</div>
                <div className="text-xs text-muted-foreground">
                  {activeKid.routineCount} routine{activeKid.routineCount !== 1 ? 's' : ''} · {activeKid.totalCheckins} lifetime check-ins
                </div>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                  <Flame className="w-5 h-5" />
                  <span className="text-2xl font-bold tabular-nums">{activeKid.currentStreakDays}</span>
                </div>
                <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">day streak</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress meter */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Today's progress</span>
            <span className="font-mono font-semibold">{completedCount} / {filtered.length} done</span>
          </div>
        )}
        {filtered.length > 0 && (
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all"
                 style={{ width: `${(completedCount / filtered.length) * 100}%` }} />
          </div>
        )}

        {myRewards.filter(r => r.earnedAt).length > 0 && (
          <Card className="border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="p-4 flex items-start gap-3">
              <Trophy className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-900 dark:text-amber-200">Reward earned! 🎉</div>
                <div className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                  {myRewards.filter(r => r.earnedAt).map(r => r.rewardLabel).join(', ')}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loadingR ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto">
                {activeTabObj?.kind === 'kid' ? <Baby className="w-6 h-6 text-muted-foreground" /> :
                 activeTabObj?.kind === 'me'  ? <User className="w-6 h-6 text-muted-foreground" /> :
                                                <Users className="w-6 h-6 text-muted-foreground" />}
              </div>
              <div className="text-sm font-medium">No routines here yet</div>
              <div className="text-xs text-muted-foreground">
                {activeTabObj?.kind === 'kid' && activeKid ? `Add a routine for ${activeKid.name} — morning, homework, bedtime.` :
                 activeTabObj?.kind === 'me' ? 'Add a personal routine just for you.' :
                                               'Add a family routine everyone participates in.'}
              </div>
              <Button size="sm" variant="outline" asChild>
                <a href="/routines/manage"><Plus className="w-4 h-4 mr-1" /> Add routine</a>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map(r => (
              <RoutineRow
                key={r.id} routine={r}
                kidFriendly={activeTabObj?.kind === 'kid'}
                onCheck={(xy) => checkMut.mutate({ routineId: r.id, kidProfileId: activeTabObj?.kidId, clickXY: xy })}
                onUncheck={() => uncheckMut.mutate(r.id)}
                busy={checkMut.isPending || uncheckMut.isPending}
              />
            ))}
          </div>
        )}

        {activeTabObj?.kind === 'kid' && activeKid && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                <Gift className="w-4 h-4 text-rose-500" /> Reward wall
              </h3>
              <Button size="sm" variant="outline" asChild>
                <a href={`/routines/manage?tab=rewards&kid=${activeKid.id}`}><Plus className="w-4 h-4 mr-1" /> Add reward</a>
              </Button>
            </div>
            <RewardList rewards={myRewards.length ? myRewards : rewards.filter(r => r.kidProfileId === activeKid.id)} homeId={homeId} />
          </div>
        )}
      </div>
    </div>
  );
}


function lateBucket(routine: { minutesLate?: number | null; timeOfDay?: string | null; completedToday?: boolean }): { ring: string; bg: string; label: string } {
  let m = routine.minutesLate ?? null;
  if (m == null && routine.timeOfDay && routine.completedToday) {
    const match = routine.timeOfDay.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const now = new Date();
      const eatMs = now.getTime() + 3 * 3600 * 1000 + (now.getTimezoneOffset() * 60 * 1000);
      const eatNow = new Date(eatMs);
      const sched = new Date(eatNow);
      sched.setUTCHours(Number(match[1]), Number(match[2]), 0, 0);
      m = Math.max(0, Math.round((eatNow.getTime() - sched.getTime()) / 60000));
    }
  }
  m = m ?? 0;
  if (m <= 0)   return { ring: 'border-green-500/60',  bg: 'bg-green-500/10',  label: 'on time' };
  if (m <= 60)  return { ring: 'border-amber-500/60',  bg: 'bg-amber-500/10',  label: 'late' };
  if (m <= 180) return { ring: 'border-orange-500/60', bg: 'bg-orange-500/10', label: 'late' };
                return { ring: 'border-red-500/60',    bg: 'bg-red-500/10',    label: 'very late' };
}

function RoutineRow({ routine, kidFriendly, onCheck, onUncheck, busy }: {
  routine: Routine; kidFriendly: boolean;
  onCheck: (xy?: { x: number; y: number }) => void;
  onUncheck: () => void; busy: boolean;
}) {
  const color = routine.color ?? '#196287';
  const streak = routine.streak ?? 0;
  const done = routine.completedToday;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (busy) return;
    const r = e.currentTarget.getBoundingClientRect();
    const xy = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    if (done) onUncheck();
    else onCheck(xy);
  };

  if (kidFriendly) {
    return (
      <button
        onClick={handleClick}
        disabled={busy}
        className={cn(
          'w-full rounded-2xl p-5 flex items-center gap-4 text-left transition-all',
          done
            ? `${lateBucket(routine).bg} border-2 ${lateBucket(routine).ring} scale-[0.98]`
            : 'bg-card border-2 border-border hover:border-sky-400 active:scale-[0.98]',
        )}
      >
        <div className={cn(
          'w-14 h-14 rounded-full flex items-center justify-center text-2xl flex-shrink-0 transition-all',
          done ? 'bg-green-500 text-white' : 'bg-muted',
        )}>
          {done ? <Check className="w-7 h-7" strokeWidth={3} /> : (routine.icon ?? '⭐')}
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn('text-lg font-bold', done && 'line-through opacity-70')}>{routine.name}</div>
          {streak > 0 && (
            <div className="flex items-center gap-1 text-sm text-orange-600 dark:text-orange-400 mt-0.5">
              <Flame className="w-4 h-4" />
              <span className="font-bold tabular-nums">{streak}-day streak</span>
            </div>
          )}
        </div>
        {done && <span className="text-2xl">🎉</span>}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={cn(
        'aspect-square p-3 rounded-xl border-2 transition-all active:scale-[0.96] flex flex-col items-center justify-between text-center relative',
        done
          ? `${lateBucket(routine).bg} ${lateBucket(routine).ring}`
          : 'bg-card border-border hover:border-sky-500 hover:shadow-md',
      )}
    >
      {streak > 0 && (
        <div className="absolute top-2 right-2 flex items-center gap-0.5 text-orange-600 dark:text-orange-400">
          <Flame className="w-3.5 h-3.5" />
          <span className="text-xs font-bold tabular-nums">{streak}</span>
        </div>
      )}
      <div className={cn(
        'w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-3xl md:text-4xl transition mt-2',
        done ? 'bg-green-500 text-white' : '',
      )}
        style={!done ? { background: color + '25' } : undefined}>
        {done ? <Check className="w-7 h-7 md:w-8 md:h-8" strokeWidth={3} /> : (routine.icon ?? '⭐')}
      </div>
      <div className="w-full">
        <div className={cn('font-semibold text-sm leading-tight line-clamp-2', done && 'line-through opacity-60')}>
          {routine.name}
        </div>
        <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-center gap-1.5 flex-wrap">
          {routine.timeOfDay && <span className="font-mono">{routine.timeOfDay}</span>}
          {routine.cadence && <span className="capitalize opacity-70">{routine.cadence}</span>}
        </div>
        {done && <div className="text-[10px] text-green-600 dark:text-green-400 font-medium mt-0.5">done 🎉</div>}
      </div>
    </button>
  );
}

function RewardList({ rewards, homeId }: { rewards: Reward[]; homeId: string }) {
  const qc = useQueryClient();
  const giveMut = useMutation({
    mutationFn: (rewardId: string) => api.post(`/homes/${homeId}/kids/rewards/${rewardId}/give`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rewards', homeId] }),
  });

  if (rewards.length === 0) {
    return (
      <Card><CardContent className="py-6 text-center text-xs text-muted-foreground">
        No rewards set up. Set goals like "7-day streak = movie night" to motivate.
      </CardContent></Card>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {rewards.map(r => {
        const earned = !!r.earnedAt && !r.givenAt;
        const given = !!r.givenAt;
        return (
          <Card key={r.id} className={cn(earned && 'border-amber-400', given && 'opacity-60')}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-500/15 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.rewardLabel}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <span>{r.streakTarget}-day streak</span>
                  {r.routineName && <><ChevronRight className="w-3 h-3" /> {r.routineName}</>}
                  {r.rewardValueMinor && <span>· KES {(r.rewardValueMinor / 100).toLocaleString()}</span>}
                </div>
              </div>
              {given ? (
                <span className="text-[10px] px-2 py-1 rounded-full bg-green-500/20 text-green-700 dark:text-green-400 font-medium">Given ✓</span>
              ) : earned ? (
                <Button size="sm" onClick={() => giveMut.mutate(r.id)} disabled={giveMut.isPending}
                        className="bg-amber-500 hover:bg-amber-600 text-white">
                  Mark given
                </Button>
              ) : (
                <span className="text-[10px] px-2 py-1 rounded-full bg-muted text-muted-foreground font-medium">In progress</span>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
